import {
    Box,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Stack,
    Typography,
    styled,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { FileText, FolderOpen, Upload } from "lucide-react";
import { useState } from "react";
import { Menu } from "../menus/Menu";
import {
    DocumentPickerValue,
    IDocumentSourceSectionProps,
} from "./DocumentPicker.types";
import { DocumentPickerModal } from "./DocumentPickerModal";
import { DocumentUploadModal } from "./DocumentUploadModal";
import { SelectedDocumentCard } from "./SelectedDocumentCard";

const SelectCard = styled(Box, {
    shouldForwardProp: (prop) => prop !== "hasError",
})<{ hasError?: boolean }>(({ theme, hasError }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: 10,
    border: `1px solid ${hasError ? theme.palette.error.main : theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    cursor: "pointer",
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    "&:hover": {
        borderColor: hasError
            ? theme.palette.error.main
            : theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.02),
    },
}));

const SelectIconBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: 8,
    flexShrink: 0,
    backgroundColor: alpha(theme.palette.grey[500], 0.08),
    color: theme.palette.grey[500],
}));

export const DocumentSourceSection = ({
    value,
    onChange,
    errorMessage,
    clientId,
}: IDocumentSourceSectionProps) => {
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(menuAnchorEl);
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [libraryModalOpen, setLibraryModalOpen] = useState(false);

    const handleConfirm = (pickerValue: DocumentPickerValue) => {
        onChange(pickerValue);
        setLibraryModalOpen(false);
        setUploadModalOpen(false);
    };

    return (
        <>
            <Menu
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
            >
                <MenuItem
                    onClick={() => {
                        handleMenuClose();
                        setUploadModalOpen(true);
                    }}
                >
                    <ListItemIcon>
                        <Upload size={16} />
                    </ListItemIcon>
                    <ListItemText>Upload from computer</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleMenuClose();
                        setLibraryModalOpen(true);
                    }}
                >
                    <ListItemIcon>
                        <FolderOpen size={16} />
                    </ListItemIcon>
                    <ListItemText>Select from document library</ListItemText>
                </MenuItem>
            </Menu>

            {value ? (
                <SelectedDocumentCard
                    value={value}
                    onRemove={() => onChange(null)}
                    onChange={handleMenuOpen}
                />
            ) : (
                <>
                    <SelectCard
                        hasError={!!errorMessage}
                        onClick={handleMenuOpen}
                    >
                        <SelectIconBox>
                            <FileText size={18} strokeWidth={2} />
                        </SelectIconBox>

                        <Stack sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: "0.8rem",
                                    fontWeight: 600,
                                    color: "text.primary",
                                }}
                            >
                                No document selected
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "0.7rem",
                                    color: "text.disabled",
                                    mt: 0.25,
                                }}
                            >
                                Choose from your library or upload a file
                            </Typography>
                        </Stack>

                        <Typography
                            component="span"
                            sx={{
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                color: "primary.main",
                                borderRadius: "4px",
                                px: 0.75,
                                py: 0.25,
                                flexShrink: 0,
                                transition: "background-color 0.15s ease",
                                ".MuiBox-root:hover &": {
                                    backgroundColor: (theme) =>
                                        alpha(theme.palette.primary.main, 0.06),
                                },
                            }}
                        >
                            Attach File
                        </Typography>
                    </SelectCard>

                    {errorMessage && (
                        <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 0.5, ml: 0.5, fontWeight: 500 }}
                        >
                            {errorMessage}
                        </Typography>
                    )}
                </>
            )}

            <DocumentPickerModal
                open={libraryModalOpen}
                onClose={() => setLibraryModalOpen(false)}
                onConfirm={handleConfirm}
                clientId={clientId}
            />
            <DocumentUploadModal
                open={uploadModalOpen}
                onClose={() => setUploadModalOpen(false)}
                onConfirm={handleConfirm}
            />
        </>
    );
};
